import { AppDispatch } from "../../store";
import {
  setFormValue,
  setNotes,
  setMetGoal,
  setObservationImage,
  toggleReferenceQuestion,
  toggleOption,
  setMiscImageDescription,
  setPulseImage,
  setManagerSignature,
  setEmployeeSignature,
} from "../../store/slices/pulseCheckSlice";
import { setObservationNotes } from "../../store/slices/observationSlice";

/**
 * Function to update Redux state with fetched observation data
 * @param observationData - Observation data fetched from API
 * @param questions - Questions data from previous fetch
 * @param dispatch - Redux dispatch function
 * @param pulseState - Current pulse state ('new', 'in-progress', 'completed')
 */
export const updatePulseCheckState = (
  observationData: any,
  questions: any,
  dispatch: AppDispatch,
  pulseState?: string
): void => {
  console.log("Updating PulseCheck state with:", observationData);

  try {
    // Type definition for question/activity lookup for better type safety
    type LookupItem = {
      id: string;
      question?: string;
      observation?: string;
      Question?: string;
      Observation?: string;
      section?: string;
    };

    // Define option type that matches the expected structure
    type Option = {
      id: string;
      label: string;
    };

    // 1. Handle selectedQuestions mapping to selectedReferenceQuestions
    if (
      observationData.selectedQuestions &&
      Array.isArray(observationData.selectedQuestions) &&
      observationData.selectedQuestions.length > 0
    ) {
      console.log(
        `Processing ${observationData.selectedQuestions.length} selectedQuestions`
      );

      // Check if Reference Question data is available from fetchedQuestions
      if (
        questions &&
        questions["Reference Question"] &&
        Array.isArray(questions["Reference Question"]) &&
        questions["Reference Question"].length > 0
      ) {
        const referenceQuestions = questions["Reference Question"];
        console.log(
          `Found ${referenceQuestions.length} Reference Questions in questions data`
        );

        // Create a lookup map for faster question matching
        const questionLookup = new Map<string, LookupItem>();
        referenceQuestions.forEach((q: any) => {
          const id = q.ID?.toString() || q.id?.toString();
          if (id) {
            questionLookup.set(id, q);
          }
        });

        // Process each selected question from the observation data
        observationData.selectedQuestions.forEach((selectedQ: any) => {
          try {
            const questionId = selectedQ.questionId?.toString();
            if (!questionId) {
              console.log("Missing questionId in selectedQuestion:", selectedQ);
              return;
            }

            // Look up the question in our reference data using the map for efficient lookup
            const matchingQuestion = questionLookup.get(questionId);
            if (matchingQuestion) {
              const questionToAdd: Option = {
                id: questionId, // Use questionId instead of id
                label: matchingQuestion.Question || "Unknown Question",
              };

              console.log("Adding reference question:", questionToAdd);
              dispatch(toggleReferenceQuestion(questionToAdd));
            } else {
              console.log(
                `Reference Question with ID ${questionId} not found in fetchQuestions data`
              );
              // Even if not found in lookup, try to add with data from the observation
              if (selectedQ.questionText) {
                const fallbackQuestion: Option = {
                  id: questionId, // Use questionId instead of id
                  label: selectedQ.questionText,
                };
                console.log(
                  "Adding fallback reference question:",
                  fallbackQuestion
                );
                dispatch(toggleReferenceQuestion(fallbackQuestion));
              }
            }
          } catch (error) {
            console.error("Error processing a selected question:", error);
          }
        });
      } else {
        console.warn(
          "No Reference Question data found in fetchQuestions response"
        );
      }
    } else {
      console.log("No selectedQuestions in observation data");
    }

    // 2. Handle selectedItems mapping to selectedOptions (activities)
    if (
      observationData.selectedItems &&
      Array.isArray(observationData.selectedItems) &&
      observationData.selectedItems.length > 0
    ) {
      console.log(
        `Processing ${observationData.selectedItems.length} selectedItems`
      );

      // Array of possible activity sections in fetchedQuestions
      const activitySections = [
        "Before Activity",
        "During Activity (PIT)",
        "During Activity (Ergo)",
        "During Activity (Process)",
        "After Activity",
      ];

      // Create activity lookup map for faster searches
      const activityLookup = new Map<
        string,
        { activity: LookupItem; section: string }
      >();
      let totalActivities = 0;

      // Build a map of all activities from all sections for more efficient lookup
      activitySections.forEach((section) => {
        if (
          questions &&
          questions[section] &&
          Array.isArray(questions[section]) &&
          questions[section].length > 0
        ) {
          questions[section].forEach((activity: any) => {
            const activityId =
              activity.ID?.toString() || activity.id?.toString();
            if (activityId) {
              activityLookup.set(activityId, { activity, section });
              totalActivities++;
            }
          });
        }
      });

      console.log(
        `Found ${totalActivities} total activities in ${activityLookup.size} unique IDs`
      );

      // Process each selected item from the observation data
      observationData.selectedItems.forEach((selectedItem: any) => {
        try {
          const observationId = selectedItem.observationId?.toString();
          if (!observationId) {
            console.log("Missing observationId in selectedItem:", selectedItem);
            return;
          }

          // Find the activity in our lookup map
          const activityInfo = activityLookup.get(observationId);
          if (activityInfo) {
            const { activity, section } = activityInfo;

            const activityToAdd: Option = {
              id: observationId, // Use observationId instead of id
              label:
                activity.Observation ||
                activity.Question ||
                `Activity ${observationId}`,
            };

            console.log(`Adding activity from ${section}:`, activityToAdd);
            dispatch(toggleOption(activityToAdd));
          } else {
            console.log(
              `Activity with observation ID ${observationId} not found in fetchQuestions data`
            );

            // Even if not found, still add it with a generic label or observationText if available
            const genericActivity: Option = {
              id: observationId, // Use observationId instead of id
              label:
                selectedItem.observationText || `Activity #${observationId}`,
            };

            console.log("Adding generic activity option:", genericActivity);
            dispatch(toggleOption(genericActivity));
          }
        } catch (error) {
          console.error("Error processing a selected item:", error);
        }
      });
    } else {
      console.log("No selectedItems in observation data");
    }

    // 3. Map form values from the observation data
    // Create a mapping of field names to their respective API keys for cleaner code
    type FormField =
      | "metric1"
      | "result1"
      | "target1"
      | "metric2"
      | "result2"
      | "goal"
      | "note1"
      | "note2";

    interface FieldMapping {
      fieldName: FormField;
      apiKey: string;
    }

    const fieldMappings: FieldMapping[] = [
      { fieldName: "metric1", apiKey: "Metrics_Discussed" },
      { fieldName: "result1", apiKey: "Last_Shift_Results" },
      { fieldName: "target1", apiKey: "Target_Results" },
      //{ fieldName: 'note1', apiKey: 'First_Check_Smart_Objective' },
      { fieldName: "metric2", apiKey: "Metrics_Discussed" },
      { fieldName: "result2", apiKey: "Actual_Results" },
      //{ fieldName: 'note2', apiKey: 'Second_Check_Smart_Objective_Results' },
      //{ fieldName: 'goal', apiKey: 'Met_Goal' }
    ];

    // Process each field mapping - using nullish coalescing to handle empty strings differently than nulls
    fieldMappings.forEach((mapping) => {
      const value = observationData[mapping.apiKey] ?? "";
      console.log(`Setting ${String(mapping.fieldName)}:`, value);
      dispatch(
        setFormValue({ field: mapping.fieldName, value: value.toString() })
      );
    });

    // 4. Add notes from First_Check_Smart_Objective and Second_Check_Smart_Objective_Results
    if (typeof observationData.First_Check_Smart_Objective === "string") {
      // Set first note from First_Check_Smart_Objective
      console.log(
        "Setting first note from First_Check_Smart_Objective:",
        observationData.First_Check_Smart_Objective
      );
      dispatch(
        setNotes({
          which: "first",
          value: observationData.First_Check_Smart_Objective || "",
        })
      );
    } else {
      dispatch(setNotes({ which: "first", value: "" }));
    }

    if (
      typeof observationData.Second_Check_Smart_Objective_Results === "string"
    ) {
      // Set second note from Second_Check_Smart_Objective_Results
      console.log(
        "Setting second note from Second_Check_Smart_Objective_Results:",
        observationData.Second_Check_Smart_Objective_Results
      );
      dispatch(
        setNotes({
          which: "second",
          value: observationData.Second_Check_Smart_Objective_Results || "",
        })
      );
    } else {
      dispatch(setNotes({ which: "second", value: "" }));
    }

    // 5. Handle Observation_Notes field and map to observation.selectedObservation.observationNotes
    if (observationData.Observation_Notes) {
      console.log("Setting observation notes:", observationData.Observation_Notes);
      dispatch(setObservationNotes(observationData.Observation_Notes));
    } else {
      console.log("No observation notes found in API response");
    }

        // 6. Set metGoal if available - handle values like "Success", "yes", "true", "1", etc.
    if (observationData.Met_Goal) {
      const metGoalValue = observationData.Met_Goal;
      // Convert various string values to boolean or null
      let metGoal: boolean | null = null;

      if (metGoalValue !== "Click Here") {
        if (typeof metGoalValue === "string") {
          if (metGoalValue.toLowerCase() === "success" ||
              metGoalValue.toLowerCase() === "yes" ||
              metGoalValue.toLowerCase() === "true" ||
              metGoalValue === "1") {
            metGoal = true;
          } else if (metGoalValue.toLowerCase() === "failure" ||
                     metGoalValue.toLowerCase() === "no" ||
                     metGoalValue.toLowerCase() === "false" ||
                     metGoalValue === "0") {
            metGoal = false;
          }
        } else if (typeof metGoalValue === "boolean") {
          metGoal = metGoalValue;
        }
      }

      console.log("Setting metGoal:", metGoal);
      dispatch(setMetGoal(metGoal));
    }
    // 7. Process any image data
    // 6. Process any image data

    // First handle legacy images (SVG and misc images), but filter out signatures
    if (
      observationData.legacyImages &&
      Array.isArray(observationData.legacyImages)
    ) {
      // Filter out signature images - we only want miscellaneous images here
      const miscellaneousImages = observationData.legacyImages.filter(
        (image: any) => {
          const isSignature =
            (image.description && image.description.includes("Signature")) ||
            (image.fileName &&
              (image.fileName.includes("signature") ||
                image.fileName.includes("manager-signature") ||
                image.fileName.includes("employee-signature")));
          return !isSignature; // Keep only non-signature images
        }
      );

      console.log(
        `Found ${miscellaneousImages.length} miscellaneous images from legacy images (excluded signatures)`
      );

      // Process the filtered list of miscellaneous images
      miscellaneousImages.forEach((image: any, index: number) => {
        if (image && image.url) {
          console.log(`Setting legacy observation image ${index}:`, image.url);
          dispatch(
            setObservationImage({
              index,
              image: {
                imageUrl: image.url,
                fileName: image.fileName || `image-${index}.jpg`,
              },
            })
          );
        }
      });
    }

    // Handle the new images array format
    if (observationData.images && Array.isArray(observationData.images)) {
      console.log(
        `Processing ${observationData.images.length} images from API response`
      );

      observationData.images.forEach((image: any) => {
        if (image && image.imageBase64) {
          // Extract file name from the URL
          let fileName = ""; // Default file name
          try {
            // Parse the URL to extract the filename
            const url = new URL(image.imageBase64);
            const pathname = url.pathname;
            // Extract only the file name portion with extension
            const fileNameMatch = pathname.match(/\/([^/]+\.[^/.]+)(?:\?|$)/);
            if (fileNameMatch && fileNameMatch[1]) {
              fileName = fileNameMatch[1];
            }
          } catch (error) {
            console.error("Error extracting filename from URL:", error);
          }

          console.log(`Setting pulse image with ID ${image.imageId}:`, {
            imageId: image.imageId.toString(),
            imagePreview: image.imageBase64,
            description: image.description || "",
            observationId: image.observationId.toString(),
          });

          dispatch(
            setPulseImage({
              imageId: image.imageId.toString(),
              imageBase64: fileName, // Using file name extracted from URL
              imagePreview: image.imageBase64, // Using the URL for preview
              description: image.description || "",
              observationId: image.observationId.toString(),
            })
          );
        }
      });
    }

    // 8. Process miscellaneous observation images
    if (
      observationData.observationImages &&
      Array.isArray(observationData.observationImages)
    ) {
      observationData.observationImages.forEach((image: any, index: number) => {
        if (image && image.imageUrl) {
          console.log(
            `Setting miscellaneous observation image ${index}:`,
            image.imageUrl
          );
          dispatch(
            setObservationImage({
              index,
              image: {
                imageUrl: image.imageUrl,
                fileName: image.fileName || `miscell-image-${index + 1}.jpg`,
              },
            })
          );
        }
      });
    }
    // 9. Process miscellaneous image descriptions
    // Always set the misc image descriptions, regardless of how they're provided
    console.log("Setting miscellaneous image descriptions");

    const description1 =
      (observationData.miscImageDescription &&
        observationData.miscImageDescription.description1) ||
      observationData.Miscell_Image_Desc ||
      "";

    const description2 =
      (observationData.miscImageDescription &&
        observationData.miscImageDescription.description2) ||
      observationData.Miscell_Image2_Desc ||
      "";

    dispatch(
      setMiscImageDescription({
        description1,
        description2,
      })
    );

    // 10. Process signature data - keep it separate from miscellaneous images
    console.log("Processing signature data");

    // Find signature images from legacyImages if they exist
    let managerSignatureFromLegacy = null;
    let employeeSignatureFromLegacy = null;

    if (
      observationData.legacyImages &&
      Array.isArray(observationData.legacyImages)
    ) {
      // Look for manager signature in legacyImages
      managerSignatureFromLegacy = observationData.legacyImages.find(
        (image: any) =>
          (image.description &&
            image.description.includes("Manager Signature")) ||
          (image.fileName && image.fileName.includes("manager-signature"))
      );

      // Look for employee signature in legacyImages
      employeeSignatureFromLegacy = observationData.legacyImages.find(
        (image: any) =>
          (image.description &&
            image.description.includes("Employee Signature")) ||
          (image.fileName && image.fileName.includes("employee-signature"))
      );
    }

    // Manager signature - prefer direct properties but fall back to legacy images
    const managerSignatureFileName =
      observationData.Manager_Signature ||
      (managerSignatureFromLegacy ? managerSignatureFromLegacy.fileName : null);

    const managerSignatureImageUrl =
      observationData.Manager_Signature_URL ||
      (managerSignatureFromLegacy
        ? managerSignatureFromLegacy.url
        : managerSignatureFileName) ||
      null;

    if (managerSignatureFileName || managerSignatureFromLegacy) {
      console.log(
        "Setting manager signature:",
        managerSignatureFileName ||
          managerSignatureFromLegacy?.fileName ||
          "from legacy image"
      );
      dispatch(
        setManagerSignature({
          fileName:
            managerSignatureFileName ||
            managerSignatureFromLegacy?.fileName ||
            "manager-signature.png",
          imageUrl: managerSignatureImageUrl,
          isNewUpload: false, // This is from API, not a new upload
        })
      );
    }

    // Employee signature - prefer direct properties but fall back to legacy images
    const employeeSignatureFileName =
      observationData.Employee_Signature ||
      (employeeSignatureFromLegacy
        ? employeeSignatureFromLegacy.fileName
        : null);

    const employeeSignatureImageUrl =
      observationData.Employee_Signature_URL ||
      (employeeSignatureFromLegacy
        ? employeeSignatureFromLegacy.url
        : employeeSignatureFileName) ||
      null;

    if (employeeSignatureFileName || employeeSignatureFromLegacy) {
      console.log(
        "Setting employee signature:",
        employeeSignatureFileName ||
          employeeSignatureFromLegacy?.fileName ||
          "from legacy image"
      );
      dispatch(
        setEmployeeSignature({
          fileName:
            employeeSignatureFileName ||
            employeeSignatureFromLegacy?.fileName ||
            "employee-signature.png",
          imageUrl: employeeSignatureImageUrl,
          isNewUpload: false, // This is from API, not a new upload
        })
      );
    }

    console.log("Successfully updated PulseCheck state from observation data");
  } catch (error) {
    console.error("Error updating PulseCheck state:", error);
  }
};
